import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Alert, Tag, Divider } from 'antd';
import { SwapOutlined, WarningOutlined } from '@ant-design/icons';
import { Factory } from '@/components/Icons';
import { defaultColors } from '@/themes/antd-theme';

/**
 * PlantSwitchConfirmDialog
 * Confirmation dialog shown to global users before switching plants
 * Warns about page reload and data loss
 */
const PlantSwitchConfirmDialog = ({
  open,
  currentPlant,
  targetPlant,
  onConfirm,
  onCancel
}) => {
  if (!currentPlant || !targetPlant) {
    return null;
  }

  const isSamePlant = currentPlant.id === targetPlant.id;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={520}
      title={
        <div className="flex items-center gap-2">
          <SwapOutlined className="text-xl" style={{ color: defaultColors.primary }} />
          <span className="text-lg font-semibold">Confirm Plant Switch</span>
        </div>
      }
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={onConfirm}
          autoFocus
          disabled={isSamePlant}
        >
          {isSamePlant ? 'Same Plant' : 'Switch Plant'}
        </Button>,
      ]}
      centered
    >
      <div className="flex flex-col gap-4 mt-4">
        {/* Warning Alert */}
        <Alert
          message="Switching plants will reload the page. Any unsaved changes will be lost."
          type="warning"
          icon={<WarningOutlined />}
          showIcon
        />

        {/* Current Plant */}
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            Current Plant:
          </p>
          <div className="flex items-center gap-2">
            <Factory className="w-5 h-5 text-gray-400" />
            <div>
              <p className="font-bold text-gray-900 dark:text-gray-100 m-0">
                {currentPlant.code}: {currentPlant.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 m-0">
                {currentPlant.city}, {currentPlant.state}
              </p>
            </div>
          </div>
        </div>

        {/* Arrow Icon */}
        <div className="text-center py-2">
          <SwapOutlined
            className="text-4xl"
            style={{ color: defaultColors.primary }}
          />
        </div>

        {/* Target Plant */}
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            Switch To:
          </p>
          <div className="flex items-center gap-2">
            <Factory
              className="w-5 h-5"
              style={{ color: defaultColors.primary }}
            />
            <div>
              <p
                className="font-bold m-0"
                style={{ color: defaultColors.primary }}
              >
                {targetPlant.code}: {targetPlant.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 m-0">
                {targetPlant.city}, {targetPlant.state}
              </p>
            </div>
          </div>
        </div>

        {/* Plant Type Tag */}
        <div>
          <Tag color={defaultColors.primary} className="px-2 py-1">
            {targetPlant.type}
          </Tag>
        </div>

        <Divider className="my-2" />

        {/* Information Text */}
        <p className="text-sm text-gray-500 dark:text-gray-400 m-0">
          After switching, all data and operations will be scoped to the selected plant.
        </p>
      </div>
    </Modal>
  );
};

PlantSwitchConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  currentPlant: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    name: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    type: PropTypes.string
  }),
  targetPlant: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    name: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    type: PropTypes.string
  }),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default PlantSwitchConfirmDialog;
